import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const CompanyTimeline = () => {
  const timelineEvents = [
    {
      year: '2017',
      quarter: 'Q3',
      title: 'Bootminds is Born',
      description: 'Founded in Bangalore by GCC operators who saw the need for transparent, India-first launch playbooks.',
      icon: 'Rocket',
      color: 'primary',
      achievements: ['Boutique advisory set up', 'Founder-led delivery model', 'First GCC diagnostic completed']
    },
    {
      year: '2018',
      quarter: 'Q1',
      title: 'Intelliswift Partnership Activated',
      description: 'Bootminds integrates with Intelliswift to combine strategy, staffing, and managed GCC operations.',
      icon: 'Handshake',
      color: 'accent',
      achievements: ['Shared delivery governance', 'Launch Blueprint beta', 'Expanded delivery hubs across India']
    },
    {
      year: '2020',
      quarter: 'Q2',
      title: 'Service Models Mature',
      description: 'Under the Intelliswift umbrella we codify transparent pricing, TA strategy workshops, and zero-risk engagement clauses.',
      icon: 'Layers',
      color: 'primary',
      achievements: ['SkillStack™ screening introduced', '25-day leadership hiring promise piloted', 'Government partnership playbooks drafted']
    },
    {
      year: '2024',
      quarter: 'Q4',
      title: 'Alliance Exit',
      description: 'Intelliswift is acquired by LTTS; Bootminds leadership exits and prepares for an independent reboot.',
      icon: 'LogOut',
      color: 'accent',
      achievements: ['Knowledge transfer completed', 'Client transition support', 'Relaunch roadmap drafted']
    },
    {
      year: '2025',
      quarter: 'Q1',
      title: 'Bootminds 2.0 Relaunch',
      description: 'Reinitiated as a service-first GCC partner with four integrated pillars and published rate cards.',
      icon: 'RefreshCw',
      color: 'primary',
      achievements: ['Transparent pricing portal released', 'Zero-risk engagement charter signed', 'CXO governance council formed']
    },
    {
      year: '2025',
      quarter: 'Q2',
      title: 'Launch Blueprint Engagements',
      description: 'First wave of independent Bootminds launch programs kicks off with dual-city GCC plans.',
      icon: 'Map',
      color: 'accent',
      achievements: ['Two Fortune 500 pilots', 'Government incentive dossiers in-flight', 'SkillStack™ cohort mobilised']
    },
    {
      year: '2025',
      quarter: 'Q4',
      title: 'Expansion & Talent Proof Points',
      description: 'Targeting five active GCC blueprints, publishing the India GCC pricing benchmark, and scaling SkillStack™ adoption.',
      icon: 'TrendingUp',
      color: 'primary',
      achievements: ['Annual pricing benchmark release', '30-member CXO council', 'Retention analytics dashboards live']
    }
  ];

  const stats = [
    { label: 'Founding Year', value: '2017', icon: 'Calendar' },
    { label: 'Intelliswift Alliance', value: '2018-24', icon: 'Link2' },
    { label: 'Service Pillars', value: '4', icon: 'Layers' },
    { label: 'Target Launches (2025-26)', value: '5', icon: 'Flag' }
  ];

  return (
    <section className="py-16" id="timeline">
      <div className="container-executive">
        {/* Timeline Introduction */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-heading font-heading-bold text-text-primary mb-6">
              Our Journey of Excellence
            </h2>
            <p className="text-lg text-text-secondary leading-relaxed mb-8">
              Our journey spans the first Bootminds chapter (2017-2024) inside Intelliswift and the 2025 relaunch as an independent, service-first partner. Every milestone is grounded in real operators, real programs, and transparent economics.
            </p>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name={stat.icon} size={24} className="text-primary" />
                  </div>
                  <div className="text-2xl font-heading font-heading-bold text-text-primary mb-1">{stat.value}</div>
                  <div className="text-sm text-text-secondary">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Timeline Visualization */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-accent to-primary transform lg:-translate-x-1/2"></div>

          <div className="space-y-12">
            {timelineEvents.map((event, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative flex items-center ${
                  index % 2 === 0 
                    ? 'lg:flex-row-reverse lg:text-right' :'lg:flex-row lg:text-left'
                } flex-row text-left`}
              >
                {/* Timeline Dot */}
                <div className={`absolute left-8 lg:left-1/2 w-4 h-4 rounded-full transform lg:-translate-x-1/2 -translate-y-1/2 border-4 border-background z-10 ${
                  event.color === 'primary' ? 'bg-primary' : 'bg-accent'
                }`}></div>

                {/* Content Card */}
                <div className={`w-full lg:w-5/12 ml-16 lg:ml-0 ${
                  index % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12'
                }`}>
                  <div className="bg-background border border-border rounded-lg p-6 hover:border-primary/30 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        event.color === 'primary' ? 'bg-primary/10' : 'bg-accent/10'
                      }`}>
                        <Icon 
                          name={event.icon} 
                          size={24} 
                          className={event.color === 'primary' ? 'text-primary' : 'text-accent'} 
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`px-3 py-1 text-sm font-body-medium rounded-full ${
                            event.color === 'primary' ?'bg-primary/10 text-primary' :'bg-accent/10 text-accent'
                          }`}>
                            {event.year} {event.quarter}
                          </span>
                        </div>
                        <h4 className="text-xl font-heading font-heading-semibold text-text-primary mb-2">
                          {event.title}
                        </h4>
                        <p className="text-text-secondary mb-4 leading-relaxed">
                          {event.description}
                        </p>
                      </div>
                    </div>

                    <div className="ml-16">
                      <div className="text-sm text-text-secondary mb-2 font-body-medium">Key Achievements:</div>
                      <ul className="space-y-1">
                        {event.achievements.map((achievement, achievementIndex) => (
                          <li key={achievementIndex} className="flex items-center space-x-2 text-sm text-text-secondary">
                            <Icon name="CheckCircle" size={14} className="text-accent flex-shrink-0" />
                            <span>{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden lg:block w-5/12"></div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Future Vision */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-lg p-8 lg:p-12">
            <div className="w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Target" size={32} className="text-white" />
            </div>
            
            <h3 className="text-2xl lg:text-3xl font-heading font-heading-bold text-text-primary mb-6">
              Roadmap 2026: Service-First GCC Authority
            </h3>
            
            <p className="text-lg text-text-secondary max-w-3xl mx-auto mb-8">
              We are focused on measurable commitments: five launch blueprints in market, a living India pricing benchmark, and SkillStack™ analytics powering every staffing engagement. The rebooted Bootminds is engineered to give CXOs clarity, speed, and optionality—not vanity metrics.
            </p>

            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="text-center">
                <div className="text-2xl font-heading font-heading-bold text-text-primary mb-2">5</div>
                <div className="text-sm text-text-secondary">Launch Blueprints in Flight</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading font-heading-bold text-text-primary mb-2">$150M</div>
                <div className="text-sm text-text-secondary">Investment Influence Goal</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading font-heading-bold text-text-primary mb-2">4</div>
                <div className="text-sm text-text-secondary">Integrated Service Pillars</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading font-heading-bold text-text-primary mb-2">25-Day</div>
                <div className="text-sm text-text-secondary">Talent SLA Maintained</div>
              </div>
            </div>

          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CompanyTimeline;